import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Executor } from '../requests/Definitions';
import { ServiceProviderExecutor } from '../requests/ServiceProviderExecutor';

/**
 * The useService hook is meant for consumers that need to perform requests that should avoid memory
 * leaks in the event that a request comes back after a component unmounts. The useService hook wraps
 * the provided executor internally so that all requests can be easily cancelled.
 *
 * This hook also provides a convenience function setData which will not update the state if the
 * component is not mounted. This keeps the consumer stateless, and delegates all data state
 * management to the useService hook. Once the state is updated in useService, it
 * provides its children with the data under an attribute, 'data'.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
function useService<T>(service: Executor<any>) {
  const _isMounted = useRef(false);
  const dataQueue: MutableRefObject<Partial<T>[]> = useRef([]);
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  const [data, setData] = useState<T>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  const cancelableExecutor = useRef<ServiceProviderExecutor<any>>(new ServiceProviderExecutor(service));

  const reset = useCallback(() => {
    dataQueue.current = [];
    if (_isMounted.current) {
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      setData(null);
    }
  }, []);

  const safelySetData = useCallback((dataPartial: Partial<T>) => {
    dataQueue.current.push(dataPartial);
    if (_isMounted.current) {
      flushQueue();
    }
  }, []);

  const flushQueue = () => {
    while (dataQueue.current.length) {
      const newData = dataQueue.current.shift();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
      setData((d: any) => ({ ...d, ...newData }));
    }
  };

  useEffect(() => {
    _isMounted.current = true;
    flushQueue();
    return () => {
      _isMounted.current = false;
      cancelableExecutor.current.cancelAll();
    };
  }, []);

  return { executor: cancelableExecutor.current, setData: safelySetData, data, reset };
}

export default useService;
